/**
 * FilterChip component for displaying active filter conditions.
 * Shows filter property, operator, and value with a remove button.
 */

import { X } from '@untitledui/icons';
import { useCallback } from 'react';

import { EntityBadgeById } from '@/lib/crm/components/badges';
import { PropertyValueLabel } from '@/lib/crm/components/properties/labels/PropertyValueLabel';
import type { FilterCondition, OperatorType } from '@/lib/crm/filters/types';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import type { FilterValue } from '@/types/crm';
import { cx } from '@/utils/cx';

import { deriveAssociationValueIds } from './filterConditionDisplay';

/**
 * Human-readable operator labels for display in chips
 */
const OPERATOR_DISPLAY: Record<OperatorType, string> = {
  equals: '=',
  not_equals: '!=',
  contains: 'contains',
  not_contains: 'does not contain',
  contains_any: 'contains any of',
  not_contains_any: 'does not contain any of',
  starts_with: 'starts with',
  ends_with: 'ends with',
  greater_than: '>',
  less_than: '<',
  greater_than_or_equal: '>=',
  less_than_or_equal: '<=',
  is_empty: 'is empty',
  is_not_empty: 'is not empty',
  in: 'is one of',
  not_in: 'is not one of',
};

/**
 * Human-readable operator labels for association filters
 */
const ASSOCIATION_OPERATOR_DISPLAY: Record<string, string> = {
  equals: 'is',
  not_equals: 'is not',
  in: 'is any of',
  not_in: 'is not any of',
  is_empty: 'is unknown',
  is_not_empty: 'is known',
};

/**
 * Render association filter value id(s) as store-hydrated entity badges.
 * EntityBadgeById resolves each entity id to its display name via the CRM
 * store (auto-fetching when missing), so a raw entity id is NEVER shown to
 * the user — the same pattern FilterConditionsTooltip uses. Hoisted to
 * module scope so it doesn't get re-defined on every FilterChip render.
 */
function AssociationValuesList({
  value,
  orgId,
}: {
  value: FilterValue;
  orgId: number;
}) {
  const ids = deriveAssociationValueIds(value);
  return (
    <span className='inline-flex items-center gap-1'>
      {ids.map((id, index) => (
        <span key={id} className='inline-flex items-center'>
          {index > 0 && <span className='text-fg-quaternary mr-1'>,</span>}
          <EntityBadgeById
            entityId={id}
            orgId={orgId}
            variant='xs'
            interactive={false}
          />
        </span>
      ))}
    </span>
  );
}

interface FilterChipProps {
  /** Property label to display */
  label: string;
  /** Operator type */
  operator: OperatorType;
  /** Filter value (optional for is_empty/is_not_empty) */
  value?: FilterValue;
  /** Property schema for value formatting */
  propertyType?: PropertyTypeSchema;
  /** Callback when remove button is clicked */
  onRemove: () => void;
  /** Callback when chip is clicked (not the remove button) */
  onClick?: () => void;
  /** Whether this chip represents an association filter */
  isAssociation?: boolean;
  /** Org id — used to hydrate association value ids into entity badges */
  orgId: number;
  /** Size variant — 'sm' for compact inline display */
  size?: 'sm' | 'md';
  /**
   * Override for the remove button's aria-label. Falls back to
   * `Remove ${label} filter`. Used by group-aware chip displays where the
   * same property can appear in multiple OR-separated groups and the
   * remove button needs to disambiguate which group it belongs to.
   */
  removeAriaLabel?: string;
}

/**
 * Individual filter chip displaying a single filter condition.
 */
export function FilterChip({
  label,
  operator,
  value,
  propertyType,
  onRemove,
  onClick,
  isAssociation,
  orgId,
  size = 'md',
  removeAriaLabel,
}: FilterChipProps) {
  const operatorDisplay = isAssociation
    ? ASSOCIATION_OPERATOR_DISPLAY[operator] || operator
    : OPERATOR_DISPLAY[operator];
  const needsValue = !['is_empty', 'is_not_empty'].includes(operator);

  const isSmall = size === 'sm';

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLSpanElement>) => {
      if (!onClick) {
        return;
      }
      if (
        e.target === e.currentTarget &&
        (e.key === 'Enter' || e.key === ' ')
      ) {
        e.preventDefault();
        onClick();
      }
    },
    [onClick]
  );

  const handleRemoveClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      onRemove();
    },
    [onRemove]
  );

  return (
    <span
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? handleKeyDown : undefined}
      className={cx(
        'inline-flex items-center bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)] rounded-md border border-[var(--color-border-secondary)]',
        isSmall ? 'gap-1 px-2 py-1 text-xs' : 'gap-1.5 px-3 py-1.5 text-sm',
        onClick && 'cursor-pointer hover:bg-primary_hover transition-colors'
      )}
    >
      <span className='font-medium'>{label}</span>
      <span className='text-fg-quaternary'>{operatorDisplay}</span>
      {needsValue &&
        value != null &&
        (isAssociation ? (
          <AssociationValuesList value={value} orgId={orgId} />
        ) : (
          <span className='text-fg-primary'>
            <PropertyValueLabel
              value={value}
              propertyType={propertyType}
              fontSize={isSmall ? 'xs' : 'sm'}
            />
          </span>
        ))}
      <button
        onClick={handleRemoveClick}
        className={cx(
          'group p-0.5 hover:bg-primary_hover rounded transition-colors cursor-pointer',
          !isSmall && 'ml-0.5'
        )}
        aria-label={removeAriaLabel ?? `Remove ${label} filter`}
      >
        <X
          className={cx(
            'text-fg-quaternary group-hover:text-fg-secondary transition-colors',
            isSmall ? 'w-3 h-3' : 'w-3.5 h-3.5'
          )}
        />
      </button>
    </span>
  );
}

/**
 * Renders one FilterChip for a condition with a stable per-condition
 * `onRemove`. Hoisted so the inline-arrow `onRemove` allocation in the
 * parent's `.map` doesn't recreate the callback on every render.
 */
export function FilterConditionChip({
  condition,
  label,
  propertyType,
  isAssociation,
  orgId,
  onRemoveCondition,
  removeAriaLabel,
}: {
  condition: FilterCondition;
  label: string;
  propertyType: PropertyTypeSchema | undefined;
  isAssociation: boolean;
  orgId: number;
  onRemoveCondition: (conditionId: string) => void;
  removeAriaLabel?: string;
}) {
  const handleRemove = useCallback(
    () => onRemoveCondition(condition.id),
    [onRemoveCondition, condition.id]
  );
  return (
    <FilterChip
      label={label}
      operator={condition.operator}
      value={condition.value}
      propertyType={propertyType}
      onRemove={handleRemove}
      isAssociation={isAssociation}
      orgId={orgId}
      removeAriaLabel={removeAriaLabel}
    />
  );
}
